/**
 * Deferred hydration for app widgets (spec: specs/worklists/site-speed-phase-3.md #1, #4).
 *
 * 1. App blocks near the viewport. sections/apps.liquid and the @app branch of
 *    sections/product-template.liquid render their blocks server-side but, when
 *    "Lazy load" is enabled, rename the vendor init attribute so the vendor script
 *    does not hydrate the widget on page load. The markup still paints and is
 *    crawlable; the vendor JS/CSS chunks, API calls and main-thread work wait until
 *    the section approaches the viewport.
 *    - Okendo reviews: data-oke-widget → data-lazy-oke-widget; restored and
 *      hydrated with window.okeWidgetApi.initWidget(el, true) (public API; the
 *      loader bootstraps core + global settings itself). Star ratings: Okendo only
 *      makes them clickable when it finds [data-oke-widget] at load time, so the
 *      click → scroll is handled here.
 *    - Rebuy widgets: data-rebuy-id → data-lazy-rebuy-id; restored and hydrated
 *      with window.Rebuy.init(), which skips widgets already marked
 *      data-initialized.
 *
 * 2. Okendo loyalty after the first interaction. The Okendo Reviews embed injects
 *    init-onsite.js (defer) which reads #oke-loyalty-settings and exits when
 *    isLaunched is false. This script runs earlier in the defer queue, so it
 *    temporarily sets isLaunched:false, then on the first interaction restores the
 *    settings and re-injects the loader, which mounts the floating "Rewards"
 *    widget and the PDP points preview with the real settings. If Okendo changes
 *    the element id or the loader logic, loyalty simply loads eagerly as before.
 */
(function () {
  'use strict';

  const ROOT_MARGIN = '600px 0px';
  const LAZY_OKENDO_WIDGET = '[data-lazy-oke-widget]';
  const LAZY_REBUY_WIDGET = '[data-lazy-rebuy-id]';
  // `wheel` rather than `scroll`: scroll also fires on scroll restoration and programmatic scrolls.
  const FIRST_INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'mousemove'];
  const REBUY_WAIT_LIMIT_MS = 15000;

  function hydrateOkendo(container) {
    const widgets = container.querySelectorAll(LAZY_OKENDO_WIDGET);
    if (!widgets.length) return;

    function init() {
      widgets.forEach(function (widget) {
        widget.setAttribute('data-oke-widget', '');
        widget.removeAttribute('data-lazy-oke-widget');
        window.okeWidgetApi.initWidget(widget, true);
      });
    }

    if (window.okeWidgetApi) {
      init();
    } else {
      document.addEventListener('oke-script-loaded', init, { once: true });
    }
  }

  function rebuyReady() {
    return !!(window.Rebuy && typeof window.Rebuy.init === 'function');
  }

  function hydrateRebuy(container) {
    const widgets = container.querySelectorAll(LAZY_REBUY_WIDGET);
    if (!widgets.length) return;

    widgets.forEach(function (widget) {
      widget.setAttribute('data-rebuy-id', widget.getAttribute('data-lazy-rebuy-id'));
      widget.removeAttribute('data-lazy-rebuy-id');
    });

    if (rebuyReady()) {
      window.Rebuy.init();
      return;
    }
    // rebuy.js arrives async through the app's ScriptTag; poll briefly until it is there.
    const startedAt = Date.now();
    const timer = setInterval(function () {
      if (rebuyReady()) {
        clearInterval(timer);
        window.Rebuy.init();
      } else if (Date.now() - startedAt > REBUY_WAIT_LIMIT_MS) {
        clearInterval(timer);
      }
    }, 200);
  }

  function hydrate(container) {
    if (container.dataset.lazyAppBlocksHydrated) return;
    container.dataset.lazyAppBlocksHydrated = 'true';
    hydrateOkendo(container);
    hydrateRebuy(container);
    container.dispatchEvent(new CustomEvent('lazy-app-blocks:hydrated', { bubbles: true }));
  }

  function observe(container) {
    if (!('IntersectionObserver' in window)) {
      hydrate(container);
      return;
    }
    const observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      hydrate(container);
    }, { rootMargin: ROOT_MARGIN });
    observer.observe(container);
  }

  function bindOkendoStarRatings() {
    if (!document.querySelector(LAZY_OKENDO_WIDGET)) return;

    document.querySelectorAll('[data-oke-star-rating]').forEach(function (starRating) {
      starRating.style.cursor = 'pointer';
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-oke-star-rating]')) return;
      const widget = document.querySelector(LAZY_OKENDO_WIDGET + ', [data-oke-widget]');
      if (!widget) return;
      const container = widget.closest('[data-lazy-app-blocks]');
      if (container) hydrate(container);
      widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function onFirstInteraction(callback) {
    let done = false;
    function handler() {
      if (done) return;
      done = true;
      FIRST_INTERACTION_EVENTS.forEach(function (eventName) {
        window.removeEventListener(eventName, handler);
      });
      callback();
    }
    FIRST_INTERACTION_EVENTS.forEach(function (eventName) {
      window.addEventListener(eventName, handler, { passive: true });
    });
  }

  function deferOkendoLoyalty() {
    const settingsElement = document.getElementById('oke-loyalty-settings');
    const loaderScript = document.querySelector('script[src*="/loyalty/js/init-onsite.js"]');
    if (!settingsElement || !loaderScript || window.okeInShopifyThemeCustomizer) return;

    const originalSettings = settingsElement.textContent;
    let settings;
    try {
      settings = JSON.parse(originalSettings);
    } catch (error) {
      return;
    }
    if (settings.isLaunched === false) return;

    settingsElement.textContent = JSON.stringify(Object.assign({}, settings, { isLaunched: false }));

    onFirstInteraction(function () {
      settingsElement.textContent = originalSettings;
      const script = document.createElement('script');
      script.src = loaderScript.src;
      document.body.appendChild(script);
    });
  }

  document.querySelectorAll('[data-lazy-app-blocks]').forEach(observe);
  bindOkendoStarRatings();
  deferOkendoLoyalty();
})();
